import React, { useMemo, useState } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Box, Typography, Divider } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme, alpha } from '@mui/material/styles';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ReferenceArea,
} from 'recharts';
import dayjs from 'dayjs';

const fmtHm = (min) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

// 주축·이송 상세 - 기본은 접힌 상태(무거운 분 단위 라인차트라 필요할 때만 펼친다).
// 분 단위 주축 최고 회전수(spindleMax)·이송 최고속도(feedMax)를 선형(linear)으로 그리고,
// 운전(RUNNING) 구간을 배경 음영(ReferenceArea)으로 깔아 "돌고 있을 때" 값이 어떻게
// 움직였는지 바로 보이게 한다.
// 가공수(partCnt, 계단형)·주축 최고 rpm 차트는 예전에 있던 EqpMinuteChart(커밋 9640234)를
// 이 안으로 되돌린 것 - 사용자가 그 모양이 더 보기 좋다고 해서 복원. feed 와 축이 겹쳐
// 셋을 한 차트에 넣으면 복잡해지므로 가공수+주축 차트를 따로 위에 두고, 기존 주축·이송
// 차트는 그대로 아래 둔다(합치지 않음).
const EqpSpindleFeedDetail = ({ metrics = [], intervals = [], date, zoomRange, loading }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(true); // 2026-10-19 사용자: 접혀 있어 못 보고 지나침 → 기본 펼침(접기는 그대로 가능)
  const dayStart = useMemo(() => dayjs(date).startOf('day'), [date]);

  const { startHour, endHour } = zoomRange || { startHour: 0, endHour: 23 };
  const windowStartMin = startHour * 60;
  const windowEndMin = Math.min(1440, (endHour + 1) * 60);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- dayStart 는 date 로부터 매번 새로 만들어지는 파생값
  const chartData = useMemo(() => metrics
    .map((m) => {
      const min = Math.round(dayjs(m.ts).diff(dayStart, 'minute', true));
      return { min, label: fmtHm(min), spindleMax: m.spindleMax, feedMax: m.feedMax, partCnt: m.partCnt };
    })
    .filter((d) => d.min >= windowStartMin && d.min <= windowEndMin)
    // 2분 넘게 비는 곳(장비 꺼짐·에이전트 중지)은 선을 끊는다 — 이어 그으면 그 사이에도 돌던 것처럼 보인다.
    .reduce((acc, d) => {
      const prev = acc[acc.length - 1];
      if (prev && d.min - prev.min > 2) acc.push({ min: prev.min + 1, spindleMax: null, feedMax: null, partCnt: null });
      acc.push(d);
      return acc;
    }, []),
  [metrics, dayStart, windowStartMin, windowEndMin]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const runningAreas = useMemo(() => intervals
    .filter((iv) => iv.typ === 'STATE' && iv.val === 'RUNNING' && iv.stDt)
    .map((iv) => {
      const stMin = Math.max(windowStartMin, dayjs(iv.stDt).diff(dayStart, 'minute', true));
      const edMin = Math.min(windowEndMin, iv.edDt
        ? dayjs(iv.edDt).diff(dayStart, 'minute', true)
        : dayjs().diff(dayStart, 'minute', true));
      return { stMin, edMin };
    })
    .filter((a) => a.edMin > a.stMin),
  [intervals, dayStart, windowStartMin, windowEndMin]);

  return (
    <Accordion
      expanded={expanded} onChange={(_, next) => setExpanded(next)}
      disableGutters variant="outlined" sx={{ '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" fontWeight={700}>가공수·주축·이송 상세</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {loading && chartData.length === 0 && (
          <Typography variant="body2" color="text.secondary">불러오는 중...</Typography>
        )}
        {!loading && chartData.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>분 단위 데이터가 없습니다.</Typography>
        )}
        {chartData.length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary">분당 가공수 · 주축 최고 rpm</Typography>
            <Box sx={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis
                    dataKey="min" type="number" domain={[windowStartMin, windowEndMin]}
                    tickFormatter={fmtHm} minTickGap={30} tick={{ fontSize: 11 }}
                  />
                  <YAxis yAxisId="part" tick={{ fontSize: 11 }} width={40} allowDecimals={false} />
                  <YAxis yAxisId="spindle" orientation="right" tick={{ fontSize: 11 }} width={50} />
                  <Tooltip
                    labelFormatter={fmtHm}
                    formatter={(value, name) => {
                      if (name === '가공수') return [`${value}개`, name];
                      if (name === '주축 최고 rpm') return [`${value}rpm`, name];
                      return [value, name];
                    }}
                  />
                  <Legend />
                  {runningAreas.map((a, i) => (
                    <ReferenceArea
                      key={`run-part-${i}`} yAxisId="part" x1={a.stMin} x2={a.edMin}
                      fill={alpha(theme.palette.success.main, 0.1)} strokeOpacity={0}
                    />
                  ))}
                  <Line
                    yAxisId="part" type="stepAfter" dataKey="partCnt" name="가공수"
                    stroke={theme.palette.primary.main} dot={false} isAnimationActive={false}
                  />
                  {/* 사용자가 monotone 의 오버슈트(실제 값보다 튀어 보이는 것)를 싫어해서 linear 로 그린다 */}
                  <Line
                    yAxisId="spindle" type="linear" dataKey="spindleMax" name="주축 최고 rpm"
                    stroke={theme.palette.secondary.main} dot={false} isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="caption" color="text.secondary">주축 최고 rpm · 이송 최고속도</Typography>
            <Box sx={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis
                    dataKey="min" type="number" domain={[windowStartMin, windowEndMin]}
                    tickFormatter={fmtHm} minTickGap={30} tick={{ fontSize: 11 }}
                  />
                  <YAxis yAxisId="spindle" tick={{ fontSize: 11 }} width={46} />
                  <YAxis yAxisId="feed" orientation="right" tick={{ fontSize: 11 }} width={46} />
                  <Tooltip labelFormatter={fmtHm} />
                  <Legend />
                  {runningAreas.map((a, i) => (
                    <ReferenceArea
                      key={`run-${i}`} yAxisId="spindle" x1={a.stMin} x2={a.edMin}
                      fill={alpha(theme.palette.success.main, 0.1)} strokeOpacity={0}
                    />
                  ))}
                  <Line
                    yAxisId="spindle" type="linear" dataKey="spindleMax" name="주축 최고 rpm"
                    stroke={theme.palette.primary.main} dot={false} isAnimationActive={false}
                  />
                  <Line
                    yAxisId="feed" type="linear" dataKey="feedMax" name="이송 최고속도"
                    stroke={theme.palette.secondary.main} dot={false} isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default EqpSpindleFeedDetail;
